/**
 * One conversation: initial load, live ledger stream, and the actions the
 * user can take on it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { agentApi, streamSession } from './agentApi';
import { announceLedgerEvents } from './dataSync';
import { mergeEvents } from './timeline';

const REFRESH_ON = new Set([
  'RUN_STARTED',
  'APPROVAL_REQUESTED',
  'APPROVAL_DECISION',
  'ACTION_EXECUTED',
  'ACTION_FAILED',
  'UNDO_EXECUTED',
  'UNDO_FAILED',
  'RUN_COMPLETED',
  'RUN_FAILED',
  'RUN_CANCELLED',
]);

const EMPTY = { session: null, events: [], actions: [], runs: [], readOnly: false };
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export function useAgentSession(sessionId) {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const lastSeq = useRef(0);
  const refreshTimer = useRef(null);
  const currentId = useRef(sessionId);

  const load = useCallback(async () => {
    if (!sessionId) return;
    const detail = await agentApi.getSession(sessionId);
    if (currentId.current !== sessionId) return;
    // An action executed or undone moments ago often arrives here before the live stream delivers it.
    announceLedgerEvents(detail.events);
    lastSeq.current = Math.max(lastSeq.current, ...detail.events.map((event) => event.seq), 0);
    setData((previous) => ({
      ...detail,
      events: mergeEvents(previous.session?.id === sessionId ? previous.events : [], detail.events),
    }));
  }, [sessionId]);

  const scheduleRefresh = useCallback(() => {
    clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => {
      load().catch((loadError) => setError(loadError.message));
    }, 250);
  }, [load]);

  useEffect(() => {
    currentId.current = sessionId;
    lastSeq.current = 0;
    setData(EMPTY);
    setError(null);
    if (!sessionId) return undefined;
    setLoading(true);
    load()
      .catch((loadError) => setError(loadError.message))
      .finally(() => setLoading(false));
    return undefined;
  }, [sessionId, load]);

  useEffect(() => {
    if (!sessionId) return undefined;
    let stopped = false;
    const controller = new AbortController();

    (async () => {
      while (!stopped) {
        try {
          await streamSession(sessionId, {
            after: lastSeq.current,
            signal: controller.signal,
            onEvent: (event) => {
              // Let the PMS screens re-read what this action (or undo) changed. This runs before the
              // seq check because a reload may already have brought the event in (announced once either way).
              announceLedgerEvents([event]);
              if (event.seq <= lastSeq.current) return;
              lastSeq.current = event.seq;
              setData((previous) => (previous.session?.id === sessionId ? { ...previous, events: mergeEvents(previous.events, [event]) } : previous));
              if (REFRESH_ON.has(event.type)) scheduleRefresh();
            },
            onState: (state) => {
              setData((previous) =>
                previous.session?.id === sessionId
                  ? {
                      ...previous,
                      session: {
                        ...previous.session,
                        status: state.status || previous.session.status,
                        title: state.title || previous.session.title,
                        pendingApprovals: state.pendingApprovals,
                      },
                    }
                  : previous,
              );
            },
          });
        } catch (streamError) {
          if (stopped) return;
          if (streamError.status === 401 || streamError.status === 404) {
            setError(streamError.message);
            return;
          }
        }
        if (!stopped) await sleep(1200);
      }
    })();

    return () => {
      stopped = true;
      controller.abort();
      clearTimeout(refreshTimer.current);
    };
  }, [sessionId, scheduleRefresh]);

  const withRefresh = useCallback(
    async (request) => {
      const result = await request;
      scheduleRefresh();
      return result;
    },
    [scheduleRefresh],
  );

  const send = useCallback(
    async (text, options = {}) => {
      setData((previous) => (previous.session ? { ...previous, session: { ...previous.session, status: 'RUNNING' } } : previous));
      return withRefresh(agentApi.sendMessage(sessionId, { text, pageContext: options.pageContext, profileId: options.profileId || null }));
    },
    [sessionId, withRefresh],
  );

  const decide = useCallback((runId, approve, note) => withRefresh(agentApi.decide(runId, approve, note)), [withRefresh]);
  const cancel = useCallback((runId) => withRefresh(agentApi.cancel(runId)), [withRefresh]);
  const undoRun = useCallback((runId, force) => withRefresh(agentApi.undoRun(runId, force)), [withRefresh]);

  const undoAction = useCallback(
    async (actionId, force) => {
      try {
        return await withRefresh(agentApi.undoAction(actionId, force));
      } catch (undoError) {
        scheduleRefresh();
        if (undoError.data?.outcome) return undoError.data;
        throw undoError;
      }
    },
    [withRefresh, scheduleRefresh],
  );

  const verify = useCallback(() => agentApi.verify(sessionId), [sessionId]);

  const activeRun = [...data.runs].reverse().find((run) => run.status === 'RUNNING' || run.status === 'AWAITING_APPROVAL') || null;

  return { ...data, loading, error, reload: load, send, decide, cancel, undoRun, undoAction, verify, activeRun };
}
